// ============================================================================
// HAI Reporting Tool - Diagnostics Page
// Troubleshoot ward assignment and Firebase issues
// ============================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getWardsByFacility } from '@/services/firestore.service';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { User, Ward } from '@/types';

export function DiagnosticsPage() {
  const { userProfile, currentUser } = useAuth();
  const [firestoreUser, setFirestoreUser] = useState<User | null>(null);
  const [facilityWards, setFacilityWards] = useState<Ward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDiagnostics();
  }, [currentUser]);

  const loadDiagnostics = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch user profile directly from Firestore
      console.log(`[Diagnostics] Fetching user ${currentUser.uid} from Firestore...`);
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        throw new Error('User document not found in Firestore!');
      }

      const userData = userDocSnap.data() as User;
      setFirestoreUser(userData);
      console.log('[Diagnostics] Firestore user data:', userData);
      console.log('[Diagnostics] Facility ID:', userData.facilityId);

      // 2. Fetch facility wards
      console.log(`[Diagnostics] Fetching wards for facility ${userData.facilityId}...`);
      if (userData.facilityId) {
        const wards = await getWardsByFacility(userData.facilityId);
        setFacilityWards(wards);
        console.log('[Diagnostics] Facility wards:', wards);
      }
    } catch (err: any) {
      console.error('[Diagnostics] Error:', err);
      setError(err.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading diagnostics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Diagnostics</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-semibold">Error:</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Firebase Auth User */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Firebase Auth User</h2>
        {currentUser ? (
          <div className="space-y-2 text-sm">
            <p>
              <strong>UID:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{currentUser.uid}</code>
            </p>
            <p>
              <strong>Email:</strong> {currentUser.email}
            </p>
          </div>
        ) : (
          <p className="text-red-600">No Firebase Auth user</p>
        )}
      </div>

      {/* AuthContext Profile */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">AuthContext Profile (useAuth hook)</h2>
        {userProfile ? (
          <div className="space-y-2 text-sm">
            <p>
              <strong>Display Name:</strong> {userProfile.displayName}
            </p>
            <p>
              <strong>Email:</strong> {userProfile.email}
            </p>
            <p>
              <strong>Role:</strong> {userProfile.role}
            </p>
            <p>
              <strong>Facility ID:</strong>
              {userProfile.facilityId ? (
                <span className="bg-blue-50 px-2 py-1 rounded font-mono text-xs ml-2">
                  {userProfile.facilityId}
                </span>
              ) : (
                <span className="text-red-600 font-semibold ml-2">NONE - User not assigned to a facility!</span>
              )}
            </p>
          </div>
        ) : (
          <p className="text-red-600">No user profile from AuthContext</p>
        )}
      </div>

      {/* Firestore User Document */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Firestore User Document</h2>
        {firestoreUser ? (
          <div className="space-y-2 text-sm">
            <p>
              <strong>Display Name:</strong> {firestoreUser.displayName}
            </p>
            <p>
              <strong>Email:</strong> {firestoreUser.email}
            </p>
            <p>
              <strong>Role:</strong> {firestoreUser.role}
            </p>
            <p>
              <strong>Facility ID:</strong>
              {firestoreUser.facilityId ? (
                <span className="bg-green-50 px-2 py-1 rounded font-mono text-xs ml-2">
                  {firestoreUser.facilityId}
                </span>
              ) : (
                <span className="text-red-600 font-semibold ml-2">NONE - User not assigned to a facility!</span>
              )}
            </p>
            <p>
              <strong>Last Updated:</strong> {new Date(firestoreUser.updatedAt).toLocaleString()}
            </p>
          </div>
        ) : (
          <p className="text-red-600">Could not fetch Firestore user document</p>
        )}
      </div>

      {/* Facility Wards */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Wards in User's Facility</h2>
        {facilityWards.length > 0 ? (
          <div className="space-y-2 text-sm">
            <p className="text-gray-600 mb-3">
              Total: <strong>{facilityWards.length} wards</strong> in this facility
            </p>
            <div className="space-y-2">
              {facilityWards.map(ward => (
                <div
                  key={ward.id}
                  className="bg-green-50 border border-green-200 rounded p-3"
                >
                  <p className="font-semibold text-green-900">{ward.name}</p>
                  <p className="text-xs text-green-700 font-mono mt-1">{ward.id}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <p className="text-yellow-700 font-semibold">⚠️ No wards in this facility!</p>
            <p className="text-yellow-600 text-sm mt-2">
              The user's facility has no active wards. An admin needs to create wards for this facility.
            </p>
          </div>
        )}
      </div>

      {/* Comparison */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Comparison: AuthContext vs Firestore</h2>
        <div className="space-y-3 text-sm">
          {userProfile && firestoreUser ? (
            <>
              <div className="border-b pb-2">
                <p className="font-semibold text-gray-900">
                  AuthContext Facility
                </p>
                <p className="text-gray-600 font-mono">
                  {userProfile.facilityId || 'NOT SET'}
                </p>
              </div>
              <div className="border-b pb-2">
                <p className="font-semibold text-gray-900">
                  Firestore Facility
                </p>
                <p className="text-gray-600 font-mono">
                  {firestoreUser.facilityId || 'NOT SET'}
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                {userProfile.facilityId === firestoreUser.facilityId ? (
                  <p className="text-blue-700 font-semibold">✅ Both have same facility ID</p>
                ) : (
                  <p className="text-blue-700">
                    ⚠️ Mismatch: AuthContext has {userProfile.facilityId || 'none'}, Firestore has{' '}
                    {firestoreUser.facilityId || 'none'}
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-red-600">Could not compare - missing data</p>
          )}
        </div>
      </div>

      {/* Action Button */}
      <div className="flex gap-2">
        <button
          onClick={loadDiagnostics}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
        >
          Refresh Diagnostics
        </button>
      </div>
    </div>
  );
}
